import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider, alpha } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import { TicketSearchProvider } from "./context/TicketSearch/TicketSearchContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import ServiceWorkerUpdate from "./components/ServiceWorkerUpdate";
import { fontFamilyDisplay, fontFamilyUI } from "./theme/typography";
import { ACCENT } from "./theme/semanticColors";

const queryClient = new QueryClient();

// Converte hex (#RRGGBB/#RGB) em "r, g, b" para usar em rgba(var(--primary-rgb), a).
// Religa o whitelabel: a navegação passa a tingir hover/active/foco com a cor do
// tenant em vez do azul-marinho fixo que estava hardcoded no CSS.
const hexToRgbTriplet = (hex) => {
  if (!hex || typeof hex !== "string") return "30, 58, 138";
  let h = hex.trim().replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return "30, 58, 138";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const App = () => {
  const [locale, setLocale] = useState();
  const defaultPrimaryLight =
    localStorage.getItem("primaryColorLight") || "#1E3A8A";
  const defaultPrimaryDark =
    localStorage.getItem("primaryColorDark") || "#60A5FA";
  const appNameLocalStorage = localStorage.getItem("appName") || "OmniPlay";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(
    preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light"
  );
  const [primaryColorLight, setPrimaryColorLight] =
    useState(defaultPrimaryLight);
  const [primaryColorDark, setPrimaryColorDark] = useState(defaultPrimaryDark);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const [loginBackgroundColor, setLoginBackgroundColor] = useState("");
  const { getPublicSetting } = useSettings();
  const [navbarHeight, setNavbarHeight] = useState(56);
  const [navbarPosition, setNavbarPosition] = useState("fixed");
  const [navbarBackground, setNavbarBackground] = useState("");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode);

          const root = document.documentElement;
          root.style.setProperty(
            "--primaryColor",
            newMode === "light" ? primaryColorLight : primaryColorDark
          );

          return newMode;
        });
      },
      setPrimaryColorLight: (color) => {
        setPrimaryColorLight(color);

        if (mode === "light") {
          const root = document.documentElement;
          root.style.setProperty("--primaryColor", color);
        }
      },
      setPrimaryColorDark: (color) => {
        setPrimaryColorDark(color);

        if (mode === "dark") {
          const root = document.documentElement;
          root.style.setProperty("--primaryColor", color);
        }
      },
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      setNavbarHeight,
      setNavbarPosition,
      setNavbarBackground,
      setLoginBackgroundColor,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      navbarHeight,
      navbarPosition,
      navbarBackground,
      loginBackgroundColor,
      mode,
    }),
    [
      mode,
      primaryColorLight,
      primaryColorDark,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      navbarHeight,
      navbarPosition,
      navbarBackground,
      loginBackgroundColor,
    ]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          // Mantém o mesmo visual da scrollbar premium global (index.css):
          // pílula arredondada translúcida flutuante (border 2px transparente +
          // background-clip cria o respiro lateral). Largura física 9px, raio 8px,
          // mesmas cores/transições em light e dark — para não divergir da global.
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "9px",
              height: "9px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light"
                ? "rgba(0, 0, 0, 0.18)"
                : "rgba(255, 255, 255, 0.16)",
              borderRadius: "8px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: mode === "light"
                  ? "rgba(0, 0, 0, 0.32)"
                  : "rgba(255, 255, 255, 0.28)",
              },
              "&:active": {
                backgroundColor: mode === "light"
                  ? "rgba(0, 0, 0, 0.42)"
                  : "rgba(255, 255, 255, 0.38)",
              },
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
          },
          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark,
              light: mode === "light" ? primaryColorLight : primaryColorDark,
              dark: mode === "light" ? primaryColorLight : primaryColorDark,
            },
            // Fonte única de cores semânticas: o tema consome o ACCENT
            // (mesmos tokens usados nos componentes) — fim dos verdes/vermelhos
            // divergentes (#4caf50 vs #10b981 etc.).
            error: { main: ACCENT.danger, dark: ACCENT.dangerDark },
            warning: { main: ACCENT.warning, dark: ACCENT.warningDark },
            success: { main: ACCENT.success, dark: ACCENT.successDark },
            info: { main: ACCENT.info, dark: ACCENT.infoDark },
            background: {
              default: mode === "light" ? "#eae6df" : "#0b141a",
              paper: mode === "light" ? "#ffffff" : "#111b21",
              contrast: mode === "light" ? "#f0f2f5" : "#202c33",
              dark: mode === "light" ? "#f0f2f5" : "#202c33",
            },
            text: {
              primary: mode === "light" ? "#111b21" : "#e9edef",
              secondary: mode === "light" ? "#667781" : "#8696a0",
              disabled: mode === "light" ? "#a0adb5" : "#54656f",
            },
            divider:
              mode === "light"
                ? "#d1d7db"
                : "#222d34",
            action: {
              active:
                mode === "light"
                  ? "#54656f"
                  : "#aebac1",
              hover:
                mode === "light"
                  ? "rgba(0, 0, 0, 0.04)"
                  : "rgba(255, 255, 255, 0.06)",
              selected:
                mode === "light"
                  ? "#f0f2f5"
                  : "#2a3942",
            },
            overlay: {
              light: "rgba(255, 255, 255, 0.7)",
              medium: "rgba(255, 255, 255, 0.5)",
              dark: "rgba(255, 255, 255, 0.2)",
            },
            backdrop: {
              light:
                mode === "light" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.5)",
              medium:
                mode === "light" ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
              dark:
                mode === "light" ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.9)",
            },
            border: {
              light: mode === "light" ? "#e9edef" : "#222d34",
              medium: mode === "light" ? "#d1d7db" : "#2a3942",
              dark: mode === "light" ? "#8696a0" : "#3b4a54",
              primary: mode === "light" ? primaryColorLight : primaryColorDark,
            },
            gradients: {
              primary: `linear-gradient(45deg, ${mode === "light" ? primaryColorLight : primaryColorDark
                } 30%, ${mode === "light" ? primaryColorLight : primaryColorDark
                } 90%)`,
              secondary: `linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)`,
              info: `linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)`,
              success: `linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)`,
              warning: `linear-gradient(45deg, #FF9800 30%, #FFC107 90%)`,
              error: `linear-gradient(45deg, #F44336 30%, #FF5252 90%)`,
            },
            shadow: {
              light:
                mode === "light"
                  ? "0 4px 14px 0 rgba(34, 41, 47, 0.1)"
                  : "0 4px 14px 0 rgba(0, 0, 0, 0.3)",
              medium:
                mode === "light"
                  ? "0 5px 15px rgba(34, 41, 47, 0.1)"
                  : "0 5px 15px rgba(0, 0, 0, 0.25)",
              dark:
                mode === "light"
                  ? "0 5px 10px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)"
                  : "0 5px 10px rgba(0,0,0,0.5), 0 6px 6px rgba(0,0,0,0.6)",
            },
          },
          borderRadius: {
            small: "5px",
            medium: "8px",
            large: "12px",
            circle: "50%",
          },
          // Radius BASE do MUI (shape) = 8px — acaba com o 4px datado que ficava
          // em Paper/Card/Dialog/Menu/inputs por default. Casa com MuiButton e o
          // token `medium`. Superfícies premium num ponto só.
          shape: { borderRadius: 8 },
          // Tokens de motion (Fase 0) — durações/easings padronizados para
          // substituir os valores mágicos (0.15/0.2/0.25/0.3s) e "transition: all".
          motion: {
            duration: { fast: 140, base: 200, slow: 260 },
            easing: {
              standard: "cubic-bezier(0.4, 0, 0.2, 1)",
              emphasized: "cubic-bezier(0.2, 0, 0, 1)",
              exit: "cubic-bezier(0.4, 0, 1, 1)",
            },
          },
          typography: {
            fontFamily: fontFamilyUI,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            h1: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.02em" },
            h2: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.02em" },
            h3: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.015em" },
            h4: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.01em" },
            h5: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.01em" },
            h6: { fontFamily: fontFamilyDisplay, fontWeight: 600, letterSpacing: "-0.01em" },
          },
          // 🎨 Correção Global: Labels não cortam mais na borda dos campos
          overrides: {
            MuiInputLabel: {
              outlined: {
                backgroundColor: "transparent",
                paddingLeft: 4,
                paddingRight: 4,
                "&$shrink": {
                  backgroundColor: "transparent",
                  paddingLeft: 6,
                  paddingRight: 6,
                },
              },
              marginDense: {
                fontSize: "0.8rem",
                transform: "translate(14px, 10px) scale(1)",
                "&$shrink": {
                  transform: "translate(14px, -6px) scale(0.75)",
                },
              },
            },
            MuiOutlinedInput: {
              marginDense: {
                fontSize: "0.8rem",
                "&:not($multiline)": {
                  height: 35,
                },
              },
              multiline: {},
              inputMarginDense: {
                paddingTop: 7,
                paddingBottom: 7,
              },
            },
            // O height: 35 acima prende chips de Autocomplete multiple: a 2ª linha vazava
            // por cima dos campos de baixo. Só libera a altura quando há chips; campos
            // sem chips e navegadores sem :has mantêm os 35px.
            MuiAutocomplete: {
              inputRoot: {
                "&.MuiOutlinedInput-marginDense:has(.MuiAutocomplete-tag)": {
                  height: "auto",
                  minHeight: 35,
                },
              },
            },
            // 🎨 Tabelas com cores adaptáveis ao tema (light/dark)
            MuiTableCell: {
              root: {
                backgroundColor: "inherit",
              },
              // 🎨 Header premium: em vez da FAIXA cinza pesada (#e9edef/#d0d0d0),
              // um fundo "whisper" + micro-label em tom mudo (text.secondary) +
              // uma única HAIRLINE de base (2px) que ancora o cabeçalho. Leitura
              // Linear/Stripe, mantendo o texto do header como está (sem CAPS).
              head: {
                backgroundColor: mode === "light" ? "#f8fafc" : "#111b21",
                color: mode === "light" ? "#667781" : "#8696a0",
                fontWeight: 600,
                fontSize: "0.78rem",
                letterSpacing: "0.01em",
                borderBottom: `2px solid ${
                  mode === "light" ? "#e4e8eb" : "#2a3942"
                }`,
              },
              stickyHeader: {
                backgroundColor: mode === "light" ? "#f8fafc" : "#111b21",
              },
              // Divisórias de corpo como hairline SUAVE (não a grade cinza cheia).
              body: {
                borderBottom: `1px solid ${
                  mode === "light" ? "#eef1f4" : "#1e2a30"
                }`,
              },
            },
            MuiTableRow: {
              root: {
                transition: "background-color 0.15s ease",
                // Hover tintado na cor da MARCA (reforça o whitelabel) — no lugar
                // do cinza neutro rgba(0,0,0,.04).
                "&:hover": {
                  backgroundColor: alpha(
                    mode === "light" ? primaryColorLight : primaryColorDark,
                    mode === "light" ? 0.04 : 0.08
                  ),
                },
              },
            },
            MuiPaper: {
              outlined: {
                borderColor: mode === "light" ? "#d1d7db" : "#222d34",
              },
              // Sombras SOFT/layered low-alpha (no lugar das cinza pesadas default
              // do MUI): contato curto + difusão suave = elevação premium.
              elevation1: {
                boxShadow:
                  mode === "light"
                    ? "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)"
                    : "0 1px 2px rgba(0,0,0,0.30)",
              },
              elevation2: {
                boxShadow:
                  mode === "light"
                    ? "0 2px 4px rgba(16,24,40,0.04), 0 4px 10px rgba(16,24,40,0.06)"
                    : "0 2px 6px rgba(0,0,0,0.35)",
              },
              elevation3: {
                boxShadow:
                  mode === "light"
                    ? "0 2px 6px rgba(16,24,40,0.05), 0 6px 14px rgba(16,24,40,0.07)"
                    : "0 3px 10px rgba(0,0,0,0.40)",
              },
              elevation4: {
                boxShadow:
                  mode === "light"
                    ? "0 4px 8px rgba(16,24,40,0.05), 0 10px 20px rgba(16,24,40,0.08)"
                    : "0 4px 14px rgba(0,0,0,0.45)",
              },
              elevation8: {
                boxShadow:
                  mode === "light"
                    ? "0 6px 12px rgba(16,24,40,0.06), 0 16px 32px rgba(16,24,40,0.10)"
                    : "0 8px 24px rgba(0,0,0,0.50)",
              },
              elevation24: {
                boxShadow:
                  mode === "light"
                    ? "0 12px 24px rgba(16,24,40,0.10), 0 24px 48px rgba(16,24,40,0.14)"
                    : "0 16px 48px rgba(0,0,0,0.60)",
              },
            },
            // ♿ Foco de teclado visível premium (Fase 0) — anel da cor da marca.
            // Mui-focusVisible só dispara via teclado (não no clique/hover),
            // então não polui o uso com mouse. Cobre Button, IconButton, MenuItem,
            // etc. (todos usam ButtonBase).
            MuiButtonBase: {
              root: {
                "&.Mui-focusVisible": {
                  boxShadow: `0 0 0 2px ${
                    mode === "light" ? "#ffffff" : "#111b21"
                  }, 0 0 0 4px ${alpha(
                    mode === "light" ? primaryColorLight : primaryColorDark,
                    0.55
                  )}`,
                },
              },
            },
            // Baseline premium de botões (Fase 0) — acaba com o UPPERCASE datado
            // do MUI e padroniza radius/peso. Estilos por componente continuam
            // vencendo; isto é só o default.
            MuiButton: {
              root: {
                textTransform: "none",
                borderRadius: 8,
                fontWeight: 600,
                "&:active": {
                  transform: "scale(0.98)",
                },
              },
              // Contained "flat premium": um sopro de sombra (não a elevação cinza
              // pesada default do MUI), um tico a mais no hover.
              contained: {
                boxShadow:
                  mode === "light" ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
                "&:hover": {
                  boxShadow:
                    mode === "light" ? "0 2px 4px rgba(16,24,40,0.12)" : "none",
                },
                "&:active": {
                  boxShadow: "none",
                },
              },
            },
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (
              appLogoDark === defaultLogoDark &&
              appLogoLight !== defaultLogoLight
            ) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (
              appLogoDark !== defaultLogoDark &&
              appLogoLight === defaultLogoLight
            ) {
              return appLogoDark;
            }
            return appLogoLight;
          },
          navbarHeight: navbarHeight || 56,
          navbarPosition: navbarPosition || "fixed",
          navbarBackground: navbarBackground || "",
        },
        locale
      ),
    [
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      locale,
      mode,
      primaryColorDark,
      primaryColorLight,
      navbarHeight,
      navbarPosition,
      navbarBackground,
    ]
  );

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    const browserLocale =
      i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || "#1E3A8A");
      })
      .catch((error) => {
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || "#60A5FA");
      })
      .catch((error) => {
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        if (file) {
          const logoUrl = getBackendUrl() + "/public/" + file;
          const img = new Image();
          img.onload = () => setAppLogoLight(logoUrl);
          img.onerror = () => {
            setAppLogoLight(defaultLogoLight);
          };
          img.src = logoUrl;
        } else {
          setAppLogoLight(defaultLogoLight);
        }
      })
      .catch((error) => {
        setAppLogoLight(defaultLogoLight);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        if (file) {
          const logoUrl = getBackendUrl() + "/public/" + file;
          const img = new Image();
          img.onload = () => setAppLogoDark(logoUrl);
          img.onerror = () => {
            setAppLogoDark(defaultLogoDark);
          };
          img.src = logoUrl;
        } else {
          setAppLogoDark(defaultLogoDark);
        }
      })
      .catch((error) => {
        setAppLogoDark(defaultLogoDark);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        if (file) {
          const faviconUrl = getBackendUrl() + "/public/" + file;
          const img = new Image();
          img.onload = () => setAppLogoFavicon(faviconUrl);
          img.onerror = () => {
            setAppLogoFavicon(defaultLogoFavicon);
          };
          img.src = faviconUrl;
        } else {
          setAppLogoFavicon(defaultLogoFavicon);
        }
      })
      .catch((error) => {
        setAppLogoFavicon(defaultLogoFavicon);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "OmniPlay");
      })
      .catch((error) => {
        setAppName("OmniPlay");
      });
    getPublicSetting("loginBackgroundColor")
      .then((color) => {
        setLoginBackgroundColor(color || "");
      })
      .catch(() => {
        setLoginBackgroundColor("");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const primary = mode === "light" ? primaryColorLight : primaryColorDark;
    root.style.setProperty("--primaryColor", primary);
    // Alimenta os tints da navegação: rgba(var(--primary-rgb), a)
    root.style.setProperty("--primary-rgb", hexToRgbTriplet(primary));
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    const root = document.documentElement;
    const p = theme.palette || {};
    root.style.setProperty(
      "--toast-bg",
      (p.background && p.background.paper) ||
      (mode === "light" ? "#ffffff" : "#111b21")
    );
    root.style.setProperty(
      "--toast-text",
      (p.text && p.text.primary) || (mode === "light" ? "#111b21" : "#e9edef")
    );
    root.style.setProperty(
      "--surface-bg",
      (p.background && p.background.paper) ||
      (mode === "light" ? "#ffffff" : "#111b21")
    );
    root.style.setProperty(
      "--surface-text",
      (p.text && p.text.primary) || (mode === "light" ? "#111b21" : "#e9edef")
    );
    const borderColor =
      (p.border && p.border.medium) ||
      p.divider ||
      (mode === "light" ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.12)");
    root.style.setProperty("--toast-border", borderColor);
    root.style.setProperty("--surface-border", borderColor);
    const boxShadow =
      (p.shadow && p.shadow.medium) ||
      (mode === "light"
        ? "0 4px 14px 0 rgba(34, 41, 47, 0.1)"
        : "0 5px 15px rgba(0,0,0,0.25)");
    root.style.setProperty("--toast-shadow", boxShadow);
    root.style.setProperty("--surface-shadow", boxShadow);
    root.style.setProperty(
      "--color-success",
      (p.success && p.success.main) || "#4caf50"
    );
    root.style.setProperty(
      "--color-error",
      (p.error && p.error.main) || "#f44336"
    );
    root.style.setProperty(
      "--color-warning",
      (p.warning && p.warning.main) || "#ff9800"
    );
    root.style.setProperty(
      "--color-info",
      (p.info && p.info.main) || "#2196f3"
    );
  }, [theme, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <link rel="icon" href={appLogoFavicon} />
        </Helmet>
        <ColorModeContext.Provider
          value={{
            toggleColorMode: colorMode.toggleColorMode,
            mode,
            setPrimaryColorLight: colorMode.setPrimaryColorLight,
            setPrimaryColorDark: colorMode.setPrimaryColorDark,
            setAppLogoLight: colorMode.setAppLogoLight,
            setAppLogoDark: colorMode.setAppLogoDark,
            setAppLogoFavicon: colorMode.setAppLogoFavicon,
            setAppName: colorMode.setAppName,
            setNavbarHeight: colorMode.setNavbarHeight,
            setNavbarPosition: colorMode.setNavbarPosition,
            setNavbarBackground: colorMode.setNavbarBackground,
            setLoginBackgroundColor: colorMode.setLoginBackgroundColor,
            appLogoLight,
            appLogoDark,
            primaryColorLight,
            primaryColorDark,
            appName,
            appLogoFavicon,
            loginBackgroundColor,
          }}
        >
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <TicketSearchProvider>
                <ActiveMenuProvider>
                  <Routes />
                </ActiveMenuProvider>
              </TicketSearchProvider>
            </QueryClientProvider>
            <ServiceWorkerUpdate />
          </ThemeProvider>
        </ColorModeContext.Provider>
      </HelmetProvider>
    </>
  );
};

export default App;
